import { Injectable } from "@angular/core";

import { TranslateService } from "@ngx-translate/core";

import { Store } from "@ngrx/store";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { from } from "rxjs";
import {
  catchError,
  exhaustMap,
  map,
  tap,
  switchMap,
  mergeMap,
} from "rxjs/operators";

import * as CardsActionTypes from "../../actions/cards/cards.actions";
import * as MessagesActionTypes from "../../actions/messages/message.actions";
import { CardsService } from "../../../shared/services/cards/cards.service";
import { PartiesService } from "../../../shared/services/parties/parties.service";
import { AppState } from "../../reducers";
import { LimitsService } from "src/app/shared/services/limits/limits.service";
import { SnackbarType } from "src/app/models/snackbar/snackbar.model";
import { TableConfigService } from "src/app/shared/services/table-config/table-config.service";

@Injectable()
export class CardsEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private cardService: CardsService,
    private partyService: PartiesService,
    private translate: TranslateService,
    private limitsService: LimitsService,
    private tableConfigService: TableConfigService
  ) {}

  getCardSystems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardSystems),
      exhaustMap((action) =>
        this.cardService.getCardSystems(action.pageReq).pipe(
          map((result) =>
            CardsActionTypes.loadCardSystems({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCardSytem",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCardLayout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardLayout),
      exhaustMap((action) =>
        this.cardService.getCardLayouts(action.pageReq).pipe(
          map((result) =>
            CardsActionTypes.loadCardLayout({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCardLayout",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCustomerContracts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardContractList),
      exhaustMap((action) =>
        this.partyService.getCustomerContracts(action.customerCode).pipe(
          map((result) =>
            CardsActionTypes.loadCardContractList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCustomerContracts",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCustomerEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardEmployeePartyList),
      exhaustMap((action) =>
        this.partyService.getCustomerEmployees(action.customerCode).pipe(
          map((result) =>
            CardsActionTypes.loadCardEmployeePartyList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCustomerEmployees",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCustomerVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardVehiclesList),
      exhaustMap((action) =>
        this.partyService.getCustomerVehicles(action.customerCode).pipe(
          map((result) =>
            CardsActionTypes.loadCardVehiclesList({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCustomerVehicles",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  createCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.createCard),
      exhaustMap((action) =>
        this.cardService.createCard(action).pipe(
          mergeMap((result) => [
            CardsActionTypes.resetCreation(),
            CardsActionTypes.getCard({ code: result?.result?.code }),
            CardsActionTypes.getCardPlafond({ code: result?.result?.code }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj("Cards.cardCreated", SnackbarType.SUCCESS)
              )
            )
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Cards.cardCreateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.updateCard),
      exhaustMap((action) =>
        this.cardService.updateCard(action.card).pipe(
          mergeMap((result) => [
            CardsActionTypes.getCard({ code: result?.result?.code }),
            CardsActionTypes.getCardPlafond({ code: result?.result?.code }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj("Cards.cardUpdated", SnackbarType.SUCCESS)
              )
            )
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  error ? error : "Cards.cardUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCardFilteredList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardFilteredList),
      switchMap((action) =>
        this.cardService
          .getFiltredCardList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              CardsActionTypes.loadCardFilteredList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CardsActionTypes.loadCardFilteredList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("Cards.loadError", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );
  getCardExcelList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardExcelList),
      switchMap((action) =>
        this.cardService
          .getFiltredCardList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.options.advancedFilteringArgs
          )
          .pipe(
            map((result) =>
              CardsActionTypes.loadCardExcelList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CardsActionTypes.loadCardFilteredList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj("Cards.loadError", SnackbarType.ERROR)
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Get Cards Table Config
  getCardTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardTableConfig),
      exhaustMap((action) =>
        this.tableConfigService
          .getConfigSettings(action.options.code, action.options.settingType)
          .pipe(
            map((result) =>
              CardsActionTypes.loadCardTableConfig({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CardsActionTypes.loadCardTableConfig({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "TableConfig.LoadError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  // Save Cards Table Config
  saveCardTableConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.saveCardTableConfig),
      exhaustMap((action) =>
        this.tableConfigService.saveConfigSettings(action.config).pipe(
          mergeMap(() => [
            CardsActionTypes.setLoadingEndState(),
            MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "TableConfig.SaveSuccess",
                SnackbarType.SUCCESS
              )
            ),
          ]),
          catchError((error: any) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "TableConfig.SaveError",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCustomerCardsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCustomerCardList),
      switchMap((action) =>
        this.cardService
          .getFiltredCardList(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs,
            action.container
          )
          .pipe(
            map((result) =>
              CardsActionTypes.loadCardFilteredList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CardsActionTypes.loadCardFilteredList({ response: [] }),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Cards.LoadCustomerCardsError",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  getCard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCard),
      exhaustMap((action) =>
        this.cardService.getCard(action.code).pipe(
          map((result) => CardsActionTypes.loadCard({ response: result })),
          catchError((error) => {
            const actions = [
              CardsActionTypes.loadCard({ response: [] }),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj("Cards.loadError", SnackbarType.ERROR)
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCardPlafond$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardPlafond),
      exhaustMap((action) =>
        this.cardService.getCardPlafond(action.code).pipe(
          map((result) =>
            CardsActionTypes.loadCardPlafond({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCardPlafond",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  updateCardPrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.updateCardPrompt),
      exhaustMap((action) =>
        this.cardService.updateCardPrompts(action).pipe(
          map((result) => CardsActionTypes.setLoadingEndState()),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.CardPromptsUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  deleteCardPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.deleteCardPrompt),
      exhaustMap((action) =>
        this.cardService.deleteCardPrompts(action).pipe(
          map((result) => CardsActionTypes.setLoadingEndState()),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Contracts.contractUpdateFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getLimitIntervals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getLimitIntervals),
      exhaustMap((action) =>
        this.limitsService.getLimitIntervals(action.pageReq).pipe(
          map((result) =>
            CardsActionTypes.loadLimitIntervals({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCardLimitIntervals",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  // Get Cards Limit Measurements
  getLimitMeasurements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getLimitMeasurements),
      exhaustMap((action) =>
        this.limitsService.getLimitMeasurements(action.pageReq).pipe(
          map((result) =>
            CardsActionTypes.loadLimitMeasurements({ response: result })
          ),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.errorLoadCardLimitMeasurements",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  getCardTransactionsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.getCardTransactionList),
      exhaustMap((action) =>
        this.cardService
          .getCardTransactions(
            action.options.paging,
            action.options.filteringArgs,
            action.options.sortingArgs
          )
          .pipe(
            map((result) =>
              CardsActionTypes.loadCardTransactionList({ response: result })
            ),
            catchError((error) => {
              const actions = [
                CardsActionTypes.setLoadingEndState(),
                MessagesActionTypes.setSnackbar(
                  this.buildSnackbarObj(
                    "Cards.errorLoadCardTransactions",
                    SnackbarType.ERROR
                  )
                ),
              ];
              return from(actions);
            })
          )
      )
    )
  );

  resetCardLimit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.resetCardLimit),
      exhaustMap((action) =>
        this.cardService.resetCardLimit(action).pipe(
          map((result) => {
            this.store$.dispatch(
              CardsActionTypes.getCard({ code: action.cardCode })
            );
            this.store$.dispatch(
              CardsActionTypes.getCardPlafond({ code: action.cardCode })
            );
            return MessagesActionTypes.setSnackbar(
              this.buildSnackbarObj(
                "Cards.limitResetSuccess",
                SnackbarType.SUCCESS
              )
            );
          }),
          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.limitResetFail",
                  SnackbarType.ERROR
                )
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  resetCardPin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CardsActionTypes.resetCardPin),
      exhaustMap((action) =>
        this.cardService.resetCardPin(action).pipe(
          mergeMap((result) => [
            CardsActionTypes.getCard({ code: action?.cardCode }),
          ]),
          tap(() =>
            this.store$.dispatch(
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj(
                  "Cards.pinResetSuccess",
                  SnackbarType.SUCCESS
                )
              )
            )
          ),

          catchError((error) => {
            const actions = [
              CardsActionTypes.setLoadingEndState(),
              MessagesActionTypes.setSnackbar(
                this.buildSnackbarObj("Cards.pinResetFail", SnackbarType.ERROR)
              ),
            ];
            return from(actions);
          })
        )
      )
    )
  );

  buildSnackbarObj(message: string, snackbarType: SnackbarType) {
    return {
      snackbar: {
        message: this.translate.instant(message),
        type: snackbarType,
      },
    };
  }
}
